import React from "react";
import { useParams, Navigate } from "react-router-dom";
import { Check } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useConversion } from "@/context/ConversionContext";
import { useSeo, useJsonLd } from "@/lib/api";
import { Section, Reveal, Eyebrow } from "@/components/common/Section";
import { CtaTrio } from "@/components/common/CtaButtons";
import Breadcrumb from "@/components/common/Breadcrumb";
import FaqAccordion from "@/components/common/FaqAccordion";
import ProductCard from "@/components/common/ProductCard";
import FinalCta from "@/components/common/FinalCta";
import { getServiceBySlug, getServiceRoute, getServicesByCategory, services } from "@/data/services";

export default function ServicePage({ slugProp }) {
  const { slug: slugParam } = useParams();
  const slug = slugProp || slugParam;
  const { t, to, lang } = useLang();
  const { openForm } = useConversion();
  const service = getServiceBySlug(slug);

  useSeo({
    lang,
    path: service ? getServiceRoute(service) : "",
    title: service ? `${service[lang].name} — NEKASSUR` : "NEKASSUR",
    description: service ? service[lang].heroText : "",
  });

  useJsonLd(
    "ld-service",
    service
      ? {
          "@context": "https://schema.org",
          "@type": "Service",
          name: service[lang].name,
          provider: { "@type": "InsuranceAgency", name: "NEKASSUR" },
          areaServed: "FR",
          description: service[lang].heroText,
        }
      : null
  );

  if (!service) return <Navigate to={to("")} replace />;

  const c = service[lang];
  const catNav = {
    particuliers: t.nav.particuliers,
    professionnels: t.nav.professionnels,
    epargne: t.nav.epargne,
  }[service.category];

  const related = getServicesByCategory(service.category).filter((s) => s.slug !== service.slug).slice(0, 3);
  const relatedFallback = related.length ? related : services.filter((s) => s.slug !== service.slug).slice(0, 3);

  return (
    <>
      {/* HERO */}
      <section className="bg-nek-navy">
        <div className="nek-container py-12 sm:py-16">
          <Breadcrumb onDark items={[{ label: catNav.label, path: catNav.path }, { label: c.name }]} />
          <div className="mt-8 grid items-center gap-10 lg:grid-cols-12">
            <Reveal className="lg:col-span-7">
              <h1 className="nek-h1 text-white" style={{ fontSize: "clamp(2.2rem,4vw,3.4rem)" }}>{c.heroTitle}</h1>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-white/70">{c.heroText}</p>
              <div className="mt-8"><CtaTrio variant="dark" /></div>
            </Reveal>
            <Reveal className="lg:col-span-5">
              <div className="overflow-hidden rounded-[24px] border border-white/10 shadow-lift">
                <img src={service.image} alt={c.name} className="h-80 w-full object-cover" loading="eager" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PROFILES */}
      <Section tone="white">
        <div className="grid gap-10 lg:grid-cols-2">
          <Reveal>
            <h2 className="nek-h2" style={{ fontSize: "clamp(1.7rem,2.6vw,2.4rem)" }}>{t.servicePage.profilesTitle}</h2>
            <ul className="mt-6 space-y-3">
              {c.profiles.map((p) => (
                <li key={p} className="flex items-start gap-3 text-nek-ink">
                  <span className="mt-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-nek-green/15 text-nek-green"><Check className="h-3.5 w-3.5" strokeWidth={3} /></span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal>
            <div className="rounded-2xl bg-nek-light p-8">
              <h2 className="text-xl font-heading font-bold text-nek-navy">{t.servicePage.risksTitle}</h2>
              <ul className="mt-5 space-y-3">
                {c.risks.map((r) => (
                  <li key={r} className="flex items-start gap-3 text-sm text-nek-ink">
                    <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-nek-blue/10 text-nek-blue"><Check className="h-3 w-3" strokeWidth={3} /></span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* COMPLEX SITUATIONS */}
      <Section tone="navy">
        <Reveal className="max-w-3xl">
          <Eyebrow className="!text-nek-teal-100">{t.diff.eyebrow}</Eyebrow>
          <h2 className="nek-h2 mt-4 text-white" style={{ fontSize: "clamp(1.8rem,3vw,2.6rem)" }}>{t.servicePage.complexTitle}</h2>
        </Reveal>
        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          {c.complex.map((x) => (
            <div key={x} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-4">
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-nek-coral text-white"><Check className="h-4 w-4" strokeWidth={3} /></span>
              <span className="text-sm font-medium text-white">{x}</span>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => openForm({ profile: "", need: c.name })} className="btn-coral mt-8" data-testid="service-complex-cta">{t.cta.presentSituation}</button>
      </Section>

      {/* METHOD */}
      <Section tone="light">
        <Reveal className="max-w-2xl"><h2 className="nek-h2" style={{ fontSize: "clamp(1.7rem,2.6vw,2.4rem)" }}>{t.servicePage.methodTitle}</h2></Reveal>
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {t.method.steps.map((step) => (
            <Reveal key={step.n} className="nek-card p-6">
              <span className="font-heading text-3xl font-extrabold text-nek-blue/30">{step.n}</span>
              <h3 className="mt-3 text-base font-heading font-bold text-nek-navy">{step.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-nek-gray">{step.text}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* PREPARE */}
      <Section tone="white">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <h2 className="nek-h2" style={{ fontSize: "clamp(1.7rem,2.6vw,2.4rem)" }}>{t.servicePage.prepareTitle}</h2>
            <p className="mt-4 text-sm italic text-nek-gray">{t.servicePage.prepareNote}</p>
          </Reveal>
          <Reveal>
            <ul className="space-y-3">
              {c.prepare.map((p, i) => (
                <li key={p} className="flex items-start gap-3 rounded-xl border border-nek-border bg-white px-4 py-3.5 text-sm text-nek-ink">
                  <span className="font-heading font-bold text-nek-blue">{String(i + 1).padStart(2, "0")}</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
        {c.riskNote && (
          <div className="mt-8 rounded-xl border border-nek-border bg-nek-light p-5 text-sm text-nek-gray">{c.riskNote}</div>
        )}
      </Section>

      {/* FAQ */}
      {c.faq?.length > 0 && (
        <Section tone="light">
          <Reveal className="mx-auto max-w-2xl text-center"><h2 className="nek-h2" style={{ fontSize: "clamp(1.7rem,2.6vw,2.4rem)" }}>{t.servicePage.faqTitle}</h2></Reveal>
          <div className="mt-8"><FaqAccordion items={c.faq} testidPrefix="service-faq" /></div>
        </Section>
      )}

      {/* RELATED */}
      <Section tone="white">
        <Reveal><h2 className="text-2xl font-heading font-bold text-nek-navy">{t.servicePage.relatedTitle}</h2></Reveal>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {relatedFallback.map((s) => <ProductCard key={s.slug} service={s} />)}
        </div>
      </Section>

      <FinalCta title={t.servicePage.ctaTitle} text={t.servicePage.ctaText} />
    </>
  );
}
